import { FormLabel, SelectV2 } from '@smartsheet/lodestar-core';
import { isToday } from 'date-fns';
import { format } from 'date-fns-tz';
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from '../../../../../../../services/ducks/schedulesDuck';
import { useTimer } from '../hooks/useTimer';
import {
  ALL_START_TIME_OPTIONS,
  getHoursMinutesFromOptionValue,
  isDateTimeInTheFuture,
  liveDateTimeStringFormat,
  locale,
  ScheduleStateDatePickerType,
  ScheduleStateSelectType,
  timezone,
} from './utils';

export const StartTime = ({
  startTime,
  setStartTime,
  startDate,
  isRequired,
}: {
  startTime: ScheduleStateSelectType;
  setStartTime: (value: ScheduleStateSelectType) => void;
  startDate: ScheduleStateDatePickerType;
  isRequired?: boolean;
}) => {
  const { t: translate } = useTranslation();

  const [liveUTCDateTime] = useTimer();

  const { value: startDateValue } = startDate;
  const { value: startTimeValue } = startTime;
  let FILITERED_START_TIME_OPTIONS = ALL_START_TIME_OPTIONS;
  if (
    startDateValue &&
    isToday(
      Date.UTC(startDateValue.year, startDateValue.month, startDateValue.day)
    )
  ) {
    FILITERED_START_TIME_OPTIONS = ALL_START_TIME_OPTIONS.filter(option => {
      const [hours, minutes] = getHoursMinutesFromOptionValue(option.value);
      return isDateTimeInTheFuture(
        startDateValue,
        hours,
        minutes,
        liveUTCDateTime,
        timezone
      );
    });
  }

  const isStartTimeValid = useMemo(() => {
    if (startTimeValue && startDateValue?.year) {
      const [hours, minutes] = getHoursMinutesFromOptionValue(
        startTimeValue.value
      );

      if (
        isDateTimeInTheFuture(
          startDateValue,
          hours,
          minutes,
          liveUTCDateTime,
          timezone
        )
      ) {
        return true;
      } else {
        return false;
      }
    }
    if (isRequired && startTimeValue?.value) {
      return true;
    }
    return false;
  }, [isRequired, liveUTCDateTime, startDateValue, startTimeValue]);

  useEffect(() => {
    setStartTime({ value: startTimeValue, valid: isStartTimeValid });
  }, [isStartTimeValid, setStartTime, startTimeValue]);

  const [hasChanged, setHasChanged] = useState(false);
  const startTimeChangeHandler = (value: Option | null) => {
    setHasChanged(true);
    setStartTime({
      value,
      valid: isStartTimeValid,
    });
  };

  const startTimeValidState =
    hasChanged && startDateValue?.year && !isStartTimeValid
      ? 'error'
      : undefined;

  return (
    <FormLabel
      label={translate('designer:triggerPanel:schedules:upsert:startTime')}
      validationMessage={translate(
        'designer:triggerPanel:schedules:upsert:startTimePast'
      )}
      validationState={startTimeValidState}
      description={`${translate(
        'designer:triggerPanel:schedules:upsert:currentUTC'
      )}: ${format(liveUTCDateTime, liveDateTimeStringFormat)}`}
    >
      {labelProps => (
        <SelectV2
          {...labelProps}
          locale={locale}
          validationState={startTimeValidState}
          placeholder={translate('timeFormat:HHMM')}
          options={FILITERED_START_TIME_OPTIONS}
          selectedOption={startTimeValue}
          onChange={startTimeChangeHandler}
          getOptionKey={(o: Option) => o.label}
          isClearable={false}
          isSearchable={true}
        />
      )}
    </FormLabel>
  );
};
